import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import i18n from 'i18n';
import { enqueueSnackbar } from 'notistack';
import {
  clearBusinessIdTimelines,
  setIsCompletionStatusLineActive,
  setIsCurrentDateIndicatorLineActive,
  setTimelinesSearchFilterValue,
} from './actions/timelines.actions';
import { copyTimeline } from './thunks/copyTimeline';
import { createNewTimeline } from './thunks/createNewTimeline';
import { createTasksFromAssignments } from './thunks/createTasksFromAssignmentsThunk';
import { getTimelines } from './thunks/getTimelines';
import { getTimelineTasks } from './thunks/getTimelineTasks';
import { loadAllBusinessIdTimelines } from './thunks/loadAllBusinessIdTimelines';
import { saveTimelineTasks } from './thunks/saveTimelineTasks';
import { setTimelineAsDeleted } from './thunks/setTimelineAsDeleted';
import { updateTimeline } from './thunks/updateTimeline';
import { updateTimelineSettings } from './thunks/updateTimelineSettings';

const tasksAdapter = createEntityAdapter();
const newTasksAdapter = createEntityAdapter();
const businessIdTimelinesAdapter = createEntityAdapter();
const timelinesAdapter = createEntityAdapter();

const initialState = {
  timelines: timelinesAdapter.getInitialState(),
  businessIdTimelines: businessIdTimelinesAdapter.getInitialState(),
  loadingAllBusinessIdTimelines: false,
  loadingTimelineImport: false,
  selectedTimelineId: null,
  tasks: tasksAdapter.getInitialState(),
  newTasks: newTasksAdapter.getInitialState(),
  taskModifications: {},
  loadingTimelines: false,
  loadingTasks: false,
  isUpdatingTimeline: false,
  linkedTasks: [],
  masterParentProjectLogo: undefined,
  isAddNewTimelineDialogOpen: false,
  timelinesZoomValue: 'week',
  isCompletionStatusLineActive: true,
  isCurrentDateIndicatorLineActive: true,
  timelinesSearchFilterValue: '',
};

export const timelineSlice = createSlice({
  name: 'timelines',
  initialState,
  reducers: {
    addNewTask: (state, action) => {
      newTasksAdapter.addOne(state.newTasks, {
        ...action.payload,
        isVisible: true,
      });
    },
    modifyTask: (state, action) => {
      const { id, changes } = action.payload;

      if (state.tasks.ids.includes(id)) {
        state.taskModifications[id] = {
          ...(state.taskModifications[id] || {}),
          ...changes,
        };
      } else if (state.newTasks.ids.includes(id)) {
        newTasksAdapter.updateOne(state.newTasks, { id, changes });
      } else {
        console.error('Task not found for modification:', id);
      }
    },
    modifyMultipleTasksAtOnce: (state, action) => {
      action.payload.forEach((taskUpdate) => {
        const { id, changes } = taskUpdate;
        if (state.tasks.ids.includes(id)) {
          state.taskModifications[id] = {
            ...(state.taskModifications[id] || {}),
            ...changes,
          };
        } else if (state.newTasks.ids.includes(id)) {
          newTasksAdapter.updateOne(state.newTasks, { id, changes });
        } else {
          console.error('Task not found for modification:', id);
        }
      });
    },
    setAsDeleted: (state, action) => {
      const setAsDeletedRecursive = (taskId) => {
        if (state.newTasks.entities[taskId]) {
          newTasksAdapter.removeOne(state.newTasks, taskId);
        } else if (state.tasks.entities[taskId]) {
          state.taskModifications[taskId] = {
            ...(state.taskModifications[taskId] || {}),
            deletedAt: new Date().toISOString(),
          };
        }
        const childTasksInTasks = Object.values(state.tasks.entities).filter(
          (task) => task.parentId === taskId
        );
        const childTasksInNewTasks = Object.values(
          state.newTasks.entities
        ).filter((task) => task.parentId === taskId);

        const combinedChildTasks = [
          ...childTasksInTasks,
          ...childTasksInNewTasks,
        ];

        combinedChildTasks.forEach((childTask) =>
          setAsDeletedRecursive(childTask.id)
        );
      };

      setAsDeletedRecursive(action.payload);
    },

    clearModificationsAndNewTasks: (state) => {
      state.taskModifications = {};
      state.newTasks = newTasksAdapter.getInitialState();
    },
    setSelectedTimelineId: (state, action) => {
      state.selectedTimelineId = action.payload;
    },
    clearSelectedTimelineId: (state) => {
      state.selectedTimelineId = null;
    },
    addTaskToLinkedTasks: (state, action) => {
      state.linkedTasks.push(action.payload);
    },
    removeTaskFromLinkedTasks: (state, action) => {
      state.linkedTasks = state.linkedTasks.filter(
        (task) => task !== action.payload
      );
    },
    setMasterParentProjectLogo: (state, action) => {
      state.masterParentProjectLogo = action.payload;
    },
    toggleTaskVisibility: (state, action) => {
      const { taskId, isVisible } = action.payload;
      state.taskModifications[taskId] = {
        ...(state.taskModifications[taskId] || {}),
        isVisible: isVisible,
      };
    },
    openAddNewTimelineDialog: (state, action) => {
      state.isAddNewTimelineDialogOpen = true;
    },
    closeAddNewTimelineDialog: (state, action) => {
      state.isAddNewTimelineDialogOpen = false;
    },
    setTimelinesZoomValue: (state, action) => {
      state.timelinesZoomValue = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTimelineTasks.pending, (state) => {
        state.loadingTasks = true;
      })
      .addCase(getTimelineTasks.fulfilled, (state, action) => {
        const visibleTasks = action.payload.map((task) => ({
          ...task,
          isVisible: true,
        }));
        tasksAdapter.setMany(state.tasks, visibleTasks);
        state.loadingTasks = false;
      })

      .addCase(getTimelineTasks.rejected, (state) => {
        state.loadingTasks = false;
      })
      .addCase(createNewTimeline.fulfilled, (state, action) => {
        timelinesAdapter.setOne(state.timelines, action.payload);
        state.isAddNewTimelineDialogOpen = false;
      })
      .addCase(saveTimelineTasks.fulfilled, (state, action) => {
        const visibleTasks = action.payload.map((task) => ({
          ...task,
          isVisible: true,
        }));
        tasksAdapter.setMany(state.tasks, visibleTasks);

        state.newTasks = newTasksAdapter.getInitialState();
        state.taskModifications = {};
        enqueueSnackbar('Aikataulu tallennettu', { variant: 'success' });
      })

      .addCase(createTasksFromAssignments.pending, (state, action) => {
        state.isUpdatingTimeline = true;
      })
      .addCase(createTasksFromAssignments.fulfilled, (state, action) => {
        const visibleTasks = action.payload.map((task) => ({
          ...task,
          isVisible: true,
        }));

        tasksAdapter.setMany(state.tasks, visibleTasks);
        enqueueSnackbar('Litterat tuotu tehtäviksi aikatauluun', {
          variant: 'success',
        });
        state.isUpdatingTimeline = false;
      })
      .addCase(createTasksFromAssignments.rejected, (state, action) => {
        state.isUpdatingTimeline = false;
        enqueueSnackbar('Litteroiden tuonti epäonnistui', { variant: 'error' });
      })
      .addCase(saveTimelineTasks.rejected, (state, action) => {
        state.newTasks = newTasksAdapter.getInitialState();
        state.taskModifications = {};
      })
      .addCase(getTimelines.pending, (state) => {
        state.loadingTimelines = true;
      })
      .addCase(getTimelines.fulfilled, (state, action) => {
        timelinesAdapter.setMany(state.timelines, action.payload);
        state.loadingTimelines = false;
      })
      .addCase(getTimelines.rejected, (state, action) => {
        state.loadingTimelines = false;
        console.error('Failed to fetch timelines:', action.payload);
      })
      .addCase(updateTimeline.fulfilled, (state, action) => {
        timelinesAdapter.setOne(state.timelines, action.payload);

        state.loadingTimelines = false;
      })
      .addCase(updateTimelineSettings.fulfilled, (state, action) => {
        timelinesAdapter.setOne(state.timelines, action.payload);

        state.loadingTimelines = false;
      })
      .addCase(setTimelineAsDeleted.fulfilled, (state, action) => {
        state.selectedTimelineId = null;

        timelinesAdapter.removeOne(state.timelines, action.payload?.id);
      })
      .addCase(loadAllBusinessIdTimelines.pending, (state) => {
        state.loadingAllBusinessIdTimelines = true;
      })
      .addCase(loadAllBusinessIdTimelines.fulfilled, (state, action) => {
        state.loadingAllBusinessIdTimelines = false;
        businessIdTimelinesAdapter.setAll(
          state.businessIdTimelines,
          action.payload
        );
      })
      .addCase(loadAllBusinessIdTimelines.rejected, (state, action) => {
        state.loadingAllBusinessIdTimelines = false;
        console.error('Failed to fetch timelines:', action.payload);
      })
      .addCase(clearBusinessIdTimelines, (state) => {
        state.businessIdTimelines =
          businessIdTimelinesAdapter.getInitialState();
      })
      .addCase(copyTimeline.pending, (state) => {
        state.loadingTimelineImport = true;
      })
      .addCase(copyTimeline.fulfilled, (state, action) => {
        state.loadingTimelineImport = false;
        console.log('action.payload', action.payload);
        if (action.payload.newTimeline) {
          timelinesAdapter.setOne(state.timelines, action.payload.newTimeline);
        }
        if (action.payload.newTasks) {
          tasksAdapter.setMany(state.tasks, action.payload.newTasks);
        }
        enqueueSnackbar(i18n.t('common.success'), { variant: 'success' });
      })
      .addCase(copyTimeline.rejected, (state, action) => {
        state.loadingTimelineImport = false;
        state.copyError = action.error;
        enqueueSnackbar(i18n.t('common.failed'), { variant: 'error' });
      })
      .addCase(setIsCompletionStatusLineActive, (state, action) => {
        state.isCompletionStatusLineActive = action.payload;
      })
      .addCase(setIsCurrentDateIndicatorLineActive, (state, action) => {
        state.isCurrentDateIndicatorLineActive = action.payload;
      })
      .addCase(setTimelinesSearchFilterValue, (state, action) => {
        state.timelinesSearchFilterValue = action.payload;
      });
  },
});

export const {
  modifyTask,
  modifyMultipleTasksAtOnce,
  addNewTask,
  clearModificationsAndNewTasks,
  setSelectedTimelineId,
  clearSelectedTimelineId,
  setAsDeleted,
  addTaskToLinkedTasks,
  removeTaskFromLinkedTasks,
  setMasterParentProjectLogo,
  toggleTaskVisibility,
} = timelineSlice.actions;
export default timelineSlice.reducer;
